import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../../music24/components/primitives/Panel';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { AddArtistModal } from '../../../accountSelector/AddArtistModal';
import type { ArtistSwitcherState } from '../../../accountSelector/useArtistSwitcher';
import { checklistMessages as messages } from '../../../onboarding/checklist/checklistMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the checklist; the tab uses the setup slice of it */
import styles from '../../../onboarding/checklist/FirstReleaseChecklist.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SetupArtistNoteProps {
  switcher: ArtistSwitcherState;
  /** The first own artist of the workspace, or null when there is none. */
  ownArtist: { id: number; name: string } | null;
  /** True while the demo artist (LaziiBee) is the active artist. */
  isDemoActive: boolean;
}

/**
 * The head note of Settings -> Setup when the tab has no own artist to show (design d6 head, new
 * copy only). With the demo artist active it says that the artist is an example and offers the
 * switch to the own artist, or "Add your artist" when there is none. With no artist at all it
 * offers "Add your artist".
 */
const SetupArtistNoteComponent: React.FC<SetupArtistNoteProps> = ({ switcher, ownArtist, isDemoActive }) => {
  const intl = useIntl();
  const { onSelect, openAdd, closeAdd, isAddOpen } = switcher;
  const switchToOwn = useCallback(() => {
    if (ownArtist !== null) {
      onSelect(ownArtist.id);
    }
  }, [onSelect, ownArtist]);

  const demoName = switcher.active?.name ?? '';
  const title = isDemoActive
    ? intl.formatMessage(messages.setupDemoTitle, { artist: demoName })
    : intl.formatMessage(messages.title);
  // An own artist with no list: an API from before 2026-10-09 answered nothing to a member.
  let body = intl.formatMessage(ownArtist === null ? messages.setupNoArtist : messages.setupMemberNote);
  if (isDemoActive) {
    body =
      ownArtist === null
        ? intl.formatMessage(messages.setupDemoBodyNoArtist)
        : intl.formatMessage(messages.setupDemoBody);
  }

  return (
    <Panel className={styles.card} data-testid={isDemoActive ? 'checklist-setup-demo' : 'checklist-setup-no-artist'}>
      <div className={styles.setupHead}>
        <div className={styles.headText}>
          <h3>{withBrandWord(title)}</h3>
          <small>{withBrandWord(body)}</small>
        </div>
        {ownArtist === null ? (
          <SMLButton onClick={openAdd}>{intl.formatMessage(messages.setupAddArtist)}</SMLButton>
        ) : null}
        {ownArtist !== null && isDemoActive ? (
          <SMLButton onClick={switchToOwn}>
            {intl.formatMessage(messages.setupDemoSwitch, { artist: ownArtist.name })}
          </SMLButton>
        ) : null}
      </div>
      <AddArtistModal isOpen={isAddOpen} onClose={closeAdd} />
    </Panel>
  );
};

export const SetupArtistNote = TypedMemo(SetupArtistNoteComponent);
